/**
 * frontend/src/pages/valuation/_nextStep.ts
 *
 * VAL-ACTOR-JOURNEY-1-SIGNER R2 — one function, one place, for "what should
 * this person do next with this engagement." Derived from the gate order
 * `_SignOffGates.tsx` enforces before Finalize/Record will accept a body:
 *   - gate 1 (_SignOffGates.tsx:96-101, `hasComputedValue`): no computed
 *     band yet -> the gate's own detail text sends the user to Methodology.
 *   - gate 3 (_SignOffGates.tsx:115-125, `hasTuningInputs`): a persisted
 *     band with no inputs hydrated in *this* session still routes back to
 *     Methodology, for the same reason gate 3 gives — Finalize freezes
 *     reviewed session inputs, not a silently-reused prior run.
 *   - gate 2 (_SignOffGates.tsx:103-113, `concluded`/`canConclude`): once a
 *     band exists, this gate's signer/owner split decides whether the next
 *     action is "sign off" or "record" (a `valuation_owner` cannot assert a
 *     conclusion of value; only `valuation_signer` can).
 *
 * `nextStep` only ever sees the persisted `ValuationEngagement` — never the
 * session's live tuning inputs or preflight result, both of which are
 * sessionStorage-only and invisible outside a mounted Methodology/Sign-off
 * screen (see `_valuationSession.ts`). It is deliberately a coarser, always-
 * available answer for a dashboard row, a shell tab, or a screen the user
 * just opened cold — not a replacement for `_SignOffGates`' own live preflight
 * read, which stays the authority once the user is actually on that screen.
 *
 * VAL-ACTOR-JOURNEY-1-SIGNER slice C: gate 3 (hasTuningInputs) means a
 * persisted band alone is never enough to sign off — this session's own
 * methodology inputs are required too. So a band with no blocking status
 * routes back to Methodology, not straight to Output; Methodology itself
 * already lands on sign-off once reviewed (ValuationTuning.tsx:437).
 */
import { VALUATION_CONCLUSION_ROLES } from '@/types/valuation'
import type { ValuationEngagement } from '@/types/valuation'
import { runCache } from './_shared'

export interface NextStep {
  label: string
  route: string
}

// Shared wording so call sites that already know their own local gate state
// (_SignOffGates' hasComputedValue text; _OverviewBlockers' empty-draft
// state) say exactly what nextStep() would say for the same situation,
// without every such call site needing the full engagement + role this
// function takes.
export const NEXT_STEP_LABELS = {
  startIntake: 'Start intake',
  continueIntake: 'Continue intake',
  methodology: 'Enter methodology inputs',
  reviewThenSignOff: 'Review methodology, then sign off',
  signOff: 'Finalize',
  recordCalculation: 'Finalize',
  view: 'View',
} as const

export function nextStep(engagement: ValuationEngagement, role: string | undefined): NextStep {
  const id = engagement.valuation_engagement_id
  const concluded = engagement.value_kind === 'concluded'
  const savedLow = concluded ? engagement.concluded_value_low : engagement.calculated_value_low

  if (engagement.status === 'signed_off') {
    return { label: NEXT_STEP_LABELS.view, route: `/valuation/${id}/output` }
  }
  if (engagement.status === 'in_cpa_review') {
    return { label: NEXT_STEP_LABELS.signOff, route: `/valuation/${id}/signoff` }
  }
  // VAL-JOURNEY-FIX-1: a run lives in sessionStorage (runCache, _shared.tsx)
  // until Record/Finalize persists it onto the engagement, so a null saved
  // band doesn't mean no band exists yet — check the session run too.
  const sessionLow = savedLow === null || savedLow === undefined
    ? runCache.load(id)?.value_low
    : undefined
  const low = savedLow ?? (typeof sessionLow === 'number' ? sessionLow : null)
  if (low !== null && low !== undefined) {
    // Gate 1 is satisfied — a band exists. A concluded engagement a non-
    // signer role can't sign is still theirs to record from Output; every
    // other case (not concluded, or a signer role that can) needs its own
    // session inputs reviewed on Methodology before sign-off will accept
    // them, so that's the next step instead.
    const canConclude = role !== undefined
      && (VALUATION_CONCLUSION_ROLES as readonly string[]).includes(role)
    if (concluded && !canConclude) {
      return { label: NEXT_STEP_LABELS.recordCalculation, route: `/valuation/${id}/output` }
    }
    return { label: NEXT_STEP_LABELS.reviewThenSignOff, route: `/valuation/${id}/tuning` }
  }
  if (engagement.status === 'intake_in_progress') {
    return { label: NEXT_STEP_LABELS.continueIntake, route: `/valuation/${id}/intake` }
  }
  return { label: NEXT_STEP_LABELS.startIntake, route: `/valuation/${id}/intake` }
}
